import { captureException } from '@sentry/browser';
import features from 'Cloud/Application/Features';
import classNames from 'clsx';
import { findIndex, path } from 'ramda';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { BuyModal } from 'reactApp/components/BuyModal/BuyModal';
import { IOldTariffsDialogProps, IOldTariffsDialogState } from 'reactApp/components/OldTariffsView/ui/OldTariffsDialog.types';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { loadFeatures } from 'reactApp/modules/features/features.thunkActions';
import { getLoadingState } from 'reactApp/modules/loading/loading.selectors';
import { GROUP_TYPES } from 'reactApp/modules/products/products.helpers';
import { initProducts, selectProductsGroup } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { initPromoTariffs as initPromoTariffsAction, promoTariffsStart } from 'reactApp/modules/promoTariffs/promoTariffs.module';
import { getDiscountPromo } from 'reactApp/modules/promoTariffs/promoTariffs.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUser } from 'reactApp/modules/user/user.thunkActions';
import { Tariff } from 'reactApp/types/Billing';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import Carousel from 'reactApp/ui/Carousel/Carousel';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { TabMenu } from 'reactApp/ui/Menu/TabMenu';
import { TabMenuItem } from 'reactApp/ui/Menu/TabMenuItem';
import { PromoCodeDialog } from 'reactApp/ui/Promocode/PromoCodeDialog';
import { TariffBuyConnected } from 'reactApp/ui/TariffBuy/TariffBuy';
import { TariffCard } from 'reactApp/ui/TariffCard/TariffCard';
import { DISKO_MONTH_PRICE, ETabsName } from 'reactApp/ui/TariffsSection/TariffSection.constants';
import { isProTab } from 'reactApp/ui/TariffsSection/TariffsSection.helpers';
import { createGaSender, sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import { EPaymentGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getMonthes, getTariffProductsByPeriods, getYearPrice } from 'reactApp/utils/Period';
import { sendTmrGoal } from 'reactApp/utils/tmr';

import ErrorMessage from './ErrorMessage/ErrorMessage';
import styles from './OldTariffsDialog.css';

const sendGa = createGaSender('home-tariffs');
// @ts-ignore
const AB_TEST_DISCOUNT_TEXT = features.abTestDiscountText.isEnabled();

const mapStateToProps = (state) => {
    const { hasError: tariffsHasError, isLoading: tariffsIsLoading } = ProductsSelectors.getLifeCycleState(state);
    // @ts-ignore
    const isPromoLoading = getLoadingState(state, [promoTariffsStart.type]);
    const discountTariffs = getDiscountPromo(state);

    const getProductById = (id) => ProductsSelectors.getProductById(state, id);

    const space = UserSelectors.getCloudSpace(state);

    const { isLoaded: isUserLoaded, isLoading: isUserLoading, hasError: isUserError } = UserSelectors.getLifeCycleState(state);

    const groupType = GROUP_TYPES.pro;

    // @ts-ignore
    const tariffGroups = [...ProductsSelectors.getTariffGroups(groupType)];
    const selectedGroup = ProductsSelectors.getSelectedGroup(state);
    const defaultGroup = ProductsSelectors.getDefaultGroup(state, groupType);
    // @ts-ignore
    const tariffs = ProductsSelectors.getNonSpecialSingleQuoteTariffs(state) as Tariff[];

    if (
        path(['2', 'id'], tariffGroups) === 'pro' &&
        tariffs.length &&
        !tariffs.filter((tariff) => tariff.products && tariff.products.some((p) => p.hasDiskO)).length
    ) {
        tariffGroups.splice(2, 1);
    }

    return {
        tariffsHasError,
        tariffsIsLoading,
        isPromoLoading,
        tariffs,
        discountTariffs,
        space,
        isUserLoaded,
        isUserLoading,
        isUserError,
        getProductById,
        tariffGroups,
        selectedGroup,
        defaultGroup,
        groupType,
    };
};

const mapDispatchToProps = (dispatch) => ({
    initProducts: () => dispatch(initProducts()),
    initPromoTariffs: () =>
        new Promise<void>((resolve, reject) => {
            dispatch(initPromoTariffsAction({ onSuccess: resolve, onError: reject }));
        }),
    loadUser: () => dispatch(loadUser()),
    loadFeatures: () => dispatch(loadFeatures()),
    selectGroup: (name) => dispatch(selectProductsGroup(name)),
});

class OldTariffsDialogComponent extends PureComponent<IOldTariffsDialogProps, IOldTariffsDialogState> {
    static defaultProps = {
        tariffs: [],
        onClose: () => {},
        discountTariffs: {},
        onSuccess: () => {},
        onProductSelect: noop,
    };

    state = {
        showError: false,
        selectedProduct: this.props.productId,
        showPromoCodeModal: false,
        success: false,
        showOtherPaymentsDialog: false,
        period6MSwitchOffForProducts: {},
        openWithTariffInUrl: false,
    };

    componentDidMount() {
        const {
            hasError,
            tariffs,
            isUserLoaded,
            isUserError,
            loadUser,
            loadFeatures,
            initProducts,
            initPromoTariffs,
            selectGroup,
            defaultGroup,
            discountTariffs,
        } = this.props;

        if (hasError) {
            return;
        }

        sendGa('show');
        sendPaymentGa({ action: EPaymentGa.tariffsModal, label: 'view' });
        // eslint-disable-next-line sonarjs/no-duplicate-string
        sendXray(['tariffs-popup', 'show']);
        sendXray(['ab', 'discount-text', AB_TEST_DISCOUNT_TEXT ? 'value' : 'percent']);
        sendXray(['ab', 'tariff-3-months', '1']);

        const shouldLoadProducts = !tariffs || !Object.keys(tariffs).length;
        const shouldLoadPromo = !discountTariffs || !Object.keys(discountTariffs).length;

        if (shouldLoadProducts) {
            initProducts();
        }

        selectGroup(defaultGroup);

        Promise.all([
            !isUserLoaded && !isUserError ? loadUser() : Promise.resolve(),
            shouldLoadPromo ? initPromoTariffs() : Promise.resolve(),
            loadFeatures(),
        ])
            .then(() => {
                sendXray(['ab', 'new-tariffs', this.props.groupType ? `group-by-${this.props.groupType}` : 'old']);
            })
            .catch(() => this.setState({ showError: true }));
    }

    componentDidUpdate() {
        this.setState({
            showError: this.props.tariffsHasError || this.props.isUserError,
        });
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            showError: true,
        });
        captureException(error, { extra: errorInfo });
    }

    getProducts = ({ tariffs }) => {
        const { period6MSwitchOffForProducts = {} } = this.state;
        const { selectedGroup } = this.props;
        const isProTabActive = isProTab(selectedGroup);

        if (isProTabActive) {
            tariffs = tariffs.filter((tariff) => tariff.products.some((p) => p.hasDiskO));
        } else if (selectedGroup === ETabsName.year) {
            tariffs = tariffs.filter((tariff) => tariff.products.some((p) => !p.hasDiskO));
        }

        return tariffs.reduce((acc, cur) => {
            const { yearProduct, monthProduct } = getTariffProductsByPeriods(cur.products, isProTabActive);
            const product =
                selectedGroup === 'year' || (isProTab(selectedGroup) && !period6MSwitchOffForProducts[cur.id]) ? yearProduct : monthProduct;

            if (!product) {
                return acc;
            }

            let saving;
            if (product.isProfessional && product.monthlyDefaultPrice) {
                saving = (product.monthlyDefaultPrice + DISKO_MONTH_PRICE) * getMonthes(product.period) - product.price;
            } else if (selectedGroup === 'year') {
                if (!monthProduct && product.monthlyDefaultPrice) {
                    saving = 12 * product.monthlyDefaultPrice - product.price;
                } else {
                    const yearPriceFull = getYearPrice(monthProduct) || 0;
                    const yearCurrentPrice = product?.discountPrice || product.price;
                    saving = yearPriceFull - yearCurrentPrice;
                }
                if (!saving && product.discountPrice === 1) {
                    saving = 2; // For tests on test tariffs with 1rub prices
                }
            }

            return [
                ...acc,
                {
                    flags: cur.flags,
                    saving,
                    gift: cur.gift,
                    isSpecial: cur.isSpecial,
                    discount: cur.discount,
                    tariffId: cur.id,
                    ...product,
                },
            ];
        }, []);
    };

    onProductSelect = (productId) => {
        sendXray(['tariffs-popup', 'product-select', productId]);
        sendPaymentGa({ action: EPaymentGa.tariffsModal, label: 'click', tariff: productId });

        this.setState({
            selectedProduct: productId,
        });

        this.props.onProductSelect(productId);
    };

    onBuyModalClose = () => {
        sendXray(['tariffs-popup', 'buy-modal', 'close']);

        if (this.state.success) {
            this.props.onClose();
        }

        this.setState({
            selectedProduct: null,
            showOtherPaymentsDialog: false,
        });
    };

    onSuccess = () => {
        const id = this.state.selectedProduct;
        const subscription = this.props.getProductById(id);

        sendTmrGoal({ goal: 'pay-success' });

        sendXray(['tariffs-popup', 'success', `id-${subscription.id}-cost-${subscription.price}-period-${subscription.period}`], null, {
            sendGaOnly: true,
        });
        sendXray(['tariffs-popup', 'success', `id-${subscription.id}`]);

        this.setState({
            success: true,
        });

        this.props.onSuccess();
    };

    handleOnPromoCodeClick = () => {
        this.setState({
            showPromoCodeModal: true,
        });
    };

    handleOnPromoCodeClose = () => {
        this.setState({
            showPromoCodeModal: false,
        });
    };

    handleOtherPaymentsLinkClick = () => {
        this.setState({
            showOtherPaymentsDialog: true,
        });
    };

    handleGroupClick = (value) => {
        sendGa('tab-click', value);
        this.props.selectGroup(value);
    };

    handlePeriodSwitch = ({ id, isSwitchOn }) =>
        this.setState((state) => ({ period6MSwitchOffForProducts: { ...state.period6MSwitchOffForProducts, [id]: !isSwitchOn } }));

    onClose = () => {
        sendXray(['tariffs-popup', 'close']);
        sendPaymentGa({ action: EPaymentGa.tariffsModal, label: 'close' });
        this.props.onClose();
    };

    retry = () => {
        this.props.initProducts();
    };

    getHeader() {
        const { isUserLoaded, selectedGroup } = this.props;

        if (isUserLoaded) {
            return selectedGroup === GROUP_TYPES.pro ? (
                <div className={styles.headerNew}>
                    <span>PRO</span>тариф
                </div>
            ) : (
                <div className={styles.headerNew}>Облачные тарифы</div>
            );
        }
    }

    renderModals = () => {
        if (this.state.selectedProduct) {
            if (!this.state.showOtherPaymentsDialog) {
                return <TariffBuyConnected id={this.state.selectedProduct} onClose={this.onBuyModalClose} onSuccess={this.onSuccess} />;
            }

            return <BuyModal id={this.state.selectedProduct} onClose={this.onBuyModalClose} onSuccess={this.onSuccess} />;
        }

        if (this.state.showPromoCodeModal) {
            return <PromoCodeDialog onClose={this.handleOnPromoCodeClose} />;
        }
    };

    renderGroupSelector = () => {
        const { tariffGroups, selectedGroup } = this.props;

        if (!tariffGroups) {
            return;
        }

        return (
            <div className={styles.menuWrapper}>
                <TabMenu>
                    {tariffGroups.map((item) => (
                        <TabMenuItem
                            name={item.name}
                            key={item.id}
                            value={item.id}
                            badge={item.badge}
                            active={selectedGroup === item.id}
                            onClick={this.handleGroupClick}
                        />
                    ))}
                </TabMenu>
            </div>
        );
    };

    renderPromoLink = () => {
        return (
            <div className={styles.promoCode}>
                <ButtonLink primary size="big" onClick={this.handleOnPromoCodeClick}>
                    Применить промокод
                </ButtonLink>
            </div>
        );
    };

    renderTariff = (
        tariff: Record<string, any>,
        index: number,
        isSelected: boolean,
        isVisible: boolean,
        positionIdx = 0,
        onSelect = (idx: any) => idx
    ) => {
        const { selectedGroup } = this.props;
        const showSave = selectedGroup === 'year';

        return (
            <div key={tariff.id + index} data-name={tariff.id}>
                <div className={styles.tariff}>
                    <TariffCard
                        onBuyClick={this.onProductSelect}
                        {...tariff}
                        id={tariff.id}
                        tariffId={tariff.tariffId}
                        space={tariff.space}
                        flags={tariff.flags}
                        price={tariff.price}
                        discountPrice={tariff.discountPrice}
                        showSaving={showSave && !!tariff.saving}
                        period={tariff.period}
                        showSaveMoney={!showSave}
                        saving={tariff.saving}
                        isNew={false}
                        onPeriodSwitch={this.handlePeriodSwitch}
                        onSelect={() => onSelect(index)}
                        isPrimary={isProTab(selectedGroup) ? false : isSelected}
                        isSelected={isProTab(selectedGroup) ? true : isSelected}
                        isVisible={isVisible}
                        index={index - positionIdx}
                        isYear={!this.state.period6MSwitchOffForProducts[tariff.tariffId]}
                        modificator={isProTab(selectedGroup) ? 'propro' : null}
                    />
                </div>
            </div>
        );
    };

    render() {
        const { tariffs, tariffsIsLoading, isUserLoading, isPromoLoading, selectedGroup } = this.props;
        const { selectedProduct, showPromoCodeModal, showError } = this.state;
        const showLoader = tariffsIsLoading || isUserLoading || isPromoLoading;
        const startIndex = findIndex((tariff) => path(['space', 'original'], tariff) === SPACE_LIST.gb_64 || tariff.isPromoCode, tariffs); // показываем тарифы от 64гб

        const products = this.getProducts({ tariffs });

        const breakpoints = isProTab(selectedGroup)
            ? [
                  {
                      maxWidth: 800,
                      showCount: 1,
                  },
                  {
                      maxWidth: 1080,
                      showCount: 2,
                  },
                  {
                      maxWidth: 1300,
                      showCount: 3,
                  },
              ]
            : [
                  {
                      maxWidth: 800,
                      showCount: 2,
                  },
                  {
                      maxWidth: 1100,
                      showCount: 3,
                  },
              ];

        return (
            <>
                <Dialog
                    open={!selectedProduct && !showPromoCodeModal}
                    size="fullscreen"
                    header={this.getHeader()}
                    onCancel={this.onClose}
                    className={classNames({
                        [styles.root_new]: true,
                        [styles['root-error']]: showError,
                    })}
                    id="tariffs"
                >
                    <Content
                        retry={this.retry}
                        buttonText="Попробовать ещё раз"
                        wrapClass={classNames({
                            [styles.contentWrapNew]: true,
                        })}
                        description={<ErrorMessage />}
                        isLoading={showLoader}
                        hasError={showError}
                        isModal={true}
                    >
                        {products.length !== 0 && (
                            <>
                                {this.renderGroupSelector()}
                                {
                                    <div className={styles.carouselWrap}>
                                        <div className={styles.carousel}>
                                            <Carousel
                                                list={[...products, ...products, ...products, ...products, ...products]}
                                                renderItem={this.renderTariff}
                                                animateItems
                                                startPosition={startIndex !== -1 ? 2 * products.length + startIndex : 2 * products.length}
                                                outerControls
                                                overflow
                                                setWidthOnResize
                                                centerControls={isProTab(selectedGroup)}
                                                breakpoints={breakpoints}
                                            />
                                        </div>
                                    </div>
                                }
                                {this.renderPromoLink()}
                            </>
                        )}
                    </Content>
                </Dialog>

                {this.renderModals()}
            </>
        );
    }
}

export const OldTariffsDialog = connect(mapStateToProps, mapDispatchToProps)(OldTariffsDialogComponent);
